import React, { useEffect, useMemo, useState } from 'react';
import {
  COLORS,
  formatWeight,
  getGaugeColor,
  getLoadRate,
  HIGH_LOAD_THRESHOLD,
} from './truck-assignments.utils';
import './manual-assignment-v4.css';

const BADGES = {
  AVAILABLE: { label: 'Disponible', tone: 'ok' },
  ALMOST_FULL: { label: 'Quasi-plein', tone: 'warning' },
  OVER: { label: 'Dépassement', tone: 'danger' },
  CURRENT: { label: 'Camion actuel', tone: 'neutral' },
};

const getBadge = (rate, isCurrent) => {
  if (isCurrent) return BADGES.CURRENT;
  if (rate > 100) return BADGES.OVER;
  if (rate >= HIGH_LOAD_THRESHOLD) return BADGES.ALMOST_FULL;
  return BADGES.AVAILABLE;
};

/**
 * Avertissements calculés côté écran pour le camion choisi ; chacun doit être
 * confirmé avant de pouvoir affecter
 */
const getWarnings = (truck, projectedRate) => {
  const warnings = [];
  if (projectedRate > 100) {
    warnings.push({
      key: 'CAPACITY',
      code: 'CAPACITÉ DÉPASSÉE',
      text: `La charge prévisionnelle atteint ${Math.round(projectedRate)} % de la capacité de ${truck.key}.`,
    });
  }
  if (!truck.timeWindowStart || !truck.timeWindowEnd) {
    warnings.push({
      key: 'NO_TIME_WINDOW',
      code: 'SANS HORAIRES',
      text: `Le camion ${truck.key} n'a pas d'heure de départ ni de fin de tournée : les heures d'arrivée seront calculées à partir de minuit. Renseignez ses horaires dans sa fiche (menu « … » du camion).`,
    });
  }
  return warnings;
};

/**
 * Modale « Affecter à un camion » (Story 5) : choix du camion avec la charge
 * actuelle → prévisionnelle, confirmation des avertissements, option
 * d'optimisation de l'ordre, puis affectation explicite.
 */
export default function ManualAssignmentModal({
  isOpen,
  onClose,
  delivery,
  deliveries,
  trucks,
  onAssign,
  isAssigning,
  autoOptimize = true,
  onAutoOptimizeChange,
}) {
  const [selectedTruckId, setSelectedTruckId] = useState(null);
  const [confirmedWarnings, setConfirmedWarnings] = useState(new Set());

  // Une commande (menu de ligne) ou plusieurs (barre de sélection)
  const targets = useMemo(
    () => (deliveries?.length ? deliveries : delivery ? [delivery] : []),
    [deliveries, delivery]
  );
  const targetsKey = targets.map((d) => d.tourPlanningId).join(',');
  const isBulk = targets.length > 1;
  const totalWeight = targets.reduce((sum, d) => sum + (d.weight || 0), 0);
  // Camion actuel : seulement si toutes les commandes y sont déjà
  const currentVehicleId =
    targets.length > 0 &&
    targets.every((d) => d.vehicleId && d.vehicleId === targets[0].vehicleId)
      ? targets[0].vehicleId
      : null;
  const currentTruck = trucks.find((t) => t.id === currentVehicleId);

  // Camions les moins chargés d'abord, camion actuel en dernier
  const options = useMemo(
    () =>
      trucks
        .map((truck) => {
          const isCurrent = truck.id === currentVehicleId;
          // Les commandes déjà sur ce camion n'ajoutent pas de charge
          const addedWeight = targets
            .filter((d) => d.vehicleId !== truck.id)
            .reduce((sum, d) => sum + (d.weight || 0), 0);
          const currentRate = getLoadRate(truck.usedCapacity, truck.capacity);
          const projectedRate = isCurrent
            ? currentRate
            : getLoadRate(truck.usedCapacity + addedWeight, truck.capacity);
          return {
            truck,
            isCurrent,
            currentRate,
            projectedRate,
            badge: getBadge(projectedRate, isCurrent),
          };
        })
        .sort(
          (a, b) =>
            Number(a.isCurrent) - Number(b.isCurrent) ||
            a.projectedRate - b.projectedRate
        ),
    [trucks, currentVehicleId, targets]
  );

  // Présélection : le premier camion disponible, hors camion actuel
  useEffect(() => {
    if (!isOpen) return;
    const firstAvailable =
      options.find(
        (o) => !o.isCurrent && o.projectedRate < HIGH_LOAD_THRESHOLD
      ) || options.find((o) => !o.isCurrent);
    setSelectedTruckId(firstAvailable?.truck.id ?? null);
    setConfirmedWarnings(new Set());
  }, [isOpen, targetsKey]);

  if (!isOpen) return null;

  const selected = options.find((o) => o.truck.id === selectedTruckId);
  const warnings = selected
    ? getWarnings(selected.truck, selected.projectedRate)
    : [];
  const allConfirmed = warnings.every((w) => confirmedWarnings.has(w.key));

  const selectTruck = (truckId) => {
    setSelectedTruckId(truckId);
    setConfirmedWarnings(new Set());
  };

  const toggleWarning = (key) =>
    setConfirmedWarnings((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const summary = isBulk
    ? [
        `${targets.length} commandes`,
        `${targets.reduce((sum, d) => sum + (d.blCount || 1), 0)} BL`,
        formatWeight(totalWeight),
        `${new Set(targets.map((d) => d.client)).size} réceptionnaire(s)`,
      ].join(' · ')
    : [
        targets[0]?.blKeys?.[0],
        targets[0]?.client,
        targets[0]?.city,
        formatWeight(totalWeight),
        targets[0]?.products,
      ]
        .filter(Boolean)
        .join(' · ');

  const confirmLabel = selected
    ? warnings.length > 0
      ? `Affecter à ${selected.truck.key} malgré ${warnings.length} alerte${
          warnings.length > 1 ? 's' : ''
        }`
      : `Affecter à ${selected.truck.key}`
    : 'Affecter';

  return (
    <div className="mam-overlay" onClick={() => !isAssigning && onClose()}>
      <div
        className="mam"
        role="dialog"
        aria-label="Affecter à un camion"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mam__head">
          <div className="mam__titles">
            <h2 className="mam__title">
              {isBulk
                ? `Affecter ${targets.length} commandes à un camion`
                : currentTruck
                  ? 'Déplacer vers un autre camion'
                  : 'Affecter à un camion'}
            </h2>
            <p className="mam__summary">{summary}</p>
          </div>
          <button
            type="button"
            className="mam__close"
            onClick={onClose}
            disabled={isAssigning}
            title="Fermer"
          >
            ✕
          </button>
        </div>

        <div className="mam__body">
          <div className="mam__block">
            <span className="mam__block-title">
              CAMION · {options.length} véhicule{options.length > 1 ? 's' : ''}
            </span>
            {options.length === 0 ? (
              <p className="mam__empty">Aucun camion disponible ce jour.</p>
            ) : (
              <div className="mam__vehicles" role="radiogroup">
                {options.map(
                  ({ truck, isCurrent, currentRate, projectedRate, badge }) => {
                    const isSelected = truck.id === selectedTruckId;
                    return (
                      <button
                        key={truck.id}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        disabled={isCurrent || isAssigning}
                        className={`mam__vehicle ${
                          isSelected ? 'mam__vehicle--selected' : ''
                        }`}
                        onClick={() => selectTruck(truck.id)}
                      >
                        <span
                          className={`mam__radio ${
                            isSelected ? 'mam__radio--checked' : ''
                          }`}
                        />
                        <span
                          className="mam__color"
                          style={{
                            backgroundColor: truck.color || COLORS.primary,
                          }}
                        />
                        <span className="mam__vehicle-main">
                          <span className="mam__vehicle-name">
                            {truck.licensePlate &&
                            truck.licensePlate !== truck.key
                              ? `${truck.key} · ${truck.licensePlate}`
                              : truck.key}
                          </span>
                          <span className="mam__vehicle-meta">
                            {[
                              truck.capacity > 0 &&
                                formatWeight(truck.capacity),
                              truck.type !== 'Non spécifié' && truck.type,
                              (!truck.timeWindowStart ||
                                !truck.timeWindowEnd) &&
                                'sans horaires',
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        </span>
                        <span className="mam__load">
                          <span className="mam__load-row">
                            <span className="mam__gauge">
                              <span
                                className="mam__gauge-fill"
                                style={{
                                  width: `${Math.min(projectedRate, 100)}%`,
                                  backgroundColor: getGaugeColor(projectedRate),
                                }}
                              />
                            </span>
                            <span className="mam__rate">
                              {Math.round(currentRate)} %
                              {!isCurrent &&
                                ` → ${Math.round(projectedRate)} %`}
                            </span>
                          </span>
                          <span
                            className={`mam__badge mam__badge--${badge.tone}`}
                          >
                            {badge.label}
                          </span>
                        </span>
                      </button>
                    );
                  }
                )}
              </div>
            )}
          </div>

          {warnings.length > 0 && (
            <div className="mam__block">
              <span className="mam__block-title">
                AVERTISSEMENTS · à confirmer un par un
              </span>
              <div className="mam__warnings">
                {warnings.map((warning) => (
                  <label key={warning.key} className="mam__warning">
                    <input
                      type="checkbox"
                      checked={confirmedWarnings.has(warning.key)}
                      onChange={() => toggleWarning(warning.key)}
                      disabled={isAssigning}
                    />
                    <span className="mam__warning-text">
                      <span className="mam__warning-code">
                        ⚠ {warning.code}
                      </span>
                      <span>{warning.text}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {onAutoOptimizeChange && (
            <label className="mam__option">
              <input
                type="checkbox"
                checked={autoOptimize}
                onChange={(e) => onAutoOptimizeChange(e.target.checked)}
                disabled={isAssigning}
              />
              <span>
                Optimiser automatiquement l&apos;ordre du camion après
                l&apos;affectation
              </span>
              <span className="mam__option-hint">
                sinon ajout en fin de tournée
              </span>
            </label>
          )}
        </div>

        <div className="mam__foot">
          <span
            className={`mam__foot-note ${
              allConfirmed ? '' : 'mam__foot-note--warning'
            }`}
          >
            {!allConfirmed
              ? `Cochez ${
                  warnings.length > 1
                    ? `les ${warnings.length} alertes`
                    : "l'alerte"
                } pour confirmer`
              : isBulk
                ? 'Ajout en fin de tournée, dans l’ordre de la liste'
                : currentTruck
                  ? `Réaffectation depuis ${currentTruck.key}`
                  : 'Première affectation'}
          </span>
          <span className="mam__spacer" />
          <button
            type="button"
            className="mam__btn"
            onClick={onClose}
            disabled={isAssigning}
          >
            Annuler
          </button>
          <button
            type="button"
            className="mam__btn mam__btn--primary"
            onClick={() => selected && onAssign(selected.truck)}
            disabled={!selected || !allConfirmed || isAssigning}
            title={
              !allConfirmed
                ? 'Confirmez les avertissements pour continuer'
                : undefined
            }
          >
            {isAssigning ? 'Affectation…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
